import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Dice5, Sparkles, Sword, BookOpen, Map as MapIcon, Crown, Flame, ScrollText } from "lucide-react";
import { useSeoTitle } from "@/lib/useSeoTitle";

// SEO landing page targeting "how to play" search intent — gameplay,
// choices, skills, dice and inventory. See /ai-dnd-game for the D&D-flavour
// positioning; this page owns general gameplay intent per the search-intent
// ownership map.

const STEPS = [
  {
    n: 1,
    icon: ScrollText,
    title: "Create a free account",
    body: "10 free turns every day, no credit card. Or skip signup and try a 5-turn trial first.",
    cta: { to: "/register", label: "Sign up — free" },
  },
  {
    n: 2,
    icon: Sword,
    title: "Make a character",
    body: "Pick a race and class, roll your stats (4d6 drop lowest, reroll any time), then write a one-line backstory. Your character is one of one.",
  },
  {
    n: 3,
    icon: MapIcon,
    title: "Choose a world",
    body: "Start in a featured setting — Forgotten Realms, Cyberpunk Sprawl, Feudal Japan, Wuxia — or invent your own. The AI builds the rest.",
    cta: { to: "/worlds", label: "Browse community worlds" },
  },
  {
    n: 4,
    icon: Dice5,
    title: "Take your first turn",
    body: "Type what you do, or pick a suggested choice. Claude, GPT-5 or Gemini narrates the outcome and asks for a dice roll when the result could go either way.",
  },
  {
    n: 5,
    icon: Crown,
    title: "Keep playing",
    body: "Your saga saves as you go. Recruit companions, gather equipment, and publish your custom world for other players to try.",
  },
];

const FEATURES = [
  { icon: Dice5, title: "Dice, when the outcome matters", body: "Occasional rolls with advantage/disadvantage when an action could plausibly fail. Light flavour, not a full rules simulation." },
  { icon: Sword, title: "HP & status effects", body: "On-screen HP and status effects during dangerous scenes so you can see the stakes." },
  { icon: BookOpen, title: "Memory Codex", body: "Important characters, events, objectives and unresolved threads are tracked and fed back to the narrator to improve continuity across long campaigns." },
  { icon: MapIcon, title: "Living world map", body: "Visited places, NPC positions and a regenerating atlas image grow as you discover new lands." },
  { icon: Sparkles, title: "AI scene art", body: "Generated illustrations every few turns keep the visual style of your saga locked in." },
  { icon: Flame, title: "Mature mode (optional)", body: "Toggle adult themes on per-saga for grittier tone. Strict 18+ gate, off by default." },
];

const FAQ = [
  {
    q: "Is TalesTalesTales really free?",
    a: "Yes — 10 turns per day on the free tier forever, no card required. Paid tiers (Apprentice / Mage / Archmage) unlock more daily turns, premium narrators, and longer scenes."
  },
  {
    q: "Which AI narrator is best?",
    a: "Claude Sonnet 4.5 (default) is tuned for long-form continuity. GPT-5 is more cinematic, Gemini 2.5 Pro is fastest. Uncensored OpenRouter narrators are available for adult roleplay."
  },
  {
    q: "Do I need to know any RPG rules to play?",
    a: "No. The AI explains every roll and offers choices each turn. If you can read English, you can play."
  },
  {
    q: "Can I bring a homebrew world?",
    a: "Yes. On the campaign-creation page, describe your setting in plain English — the AI handles tone and creatures. You can also publish it to the community library."
  },
  {
    q: "What happens if my character is defeated?",
    a: "Permadeath is opt-in. By default, falling to 0 HP triggers a death-save sequence; three failures ends the saga."
  },
];

export default function HowToPlay() {
  useSeoTitle({
    title: "How to Play | TalesTalesTales",
    description: "How to play TalesTalesTales: create a character, pick a world, take your first turn, and use choices, dice, skills, inventory and objectives as the story grows.",
    path: "/how-to-play",
  });

  return (
    <div data-testid="how-to-play-page" className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <main id="main-content" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        {/* ===== Hero ===== */}
        <header className="text-center mb-14">
          <p className="font-ui text-[11px] uppercase tracking-[0.35em] text-amber-400/80 mb-4">
            tutorial · 2 minutes to your first choice
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 leading-tight">
            How to play <span className="gold-text">TalesTalesTales</span>
          </h1>
          <p className="font-body text-amber-100/75 mt-5 max-w-2xl mx-auto leading-relaxed">
            TalesTalesTales is a free interactive AI Novel with light RPG elements. Type what you do,
            take the occasional dice roll, and keep track of your inventory and objectives as the
            story grows. Here&apos;s how to start your first saga in five steps.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/trial">
              <Button
                data-testid="howto-trial-btn"
                className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui px-6 h-12"
              >
                <Flame className="w-4 h-4 mr-2" /> Try 5 turns — no signup
              </Button>
            </Link>
            <Link to="/register">
              <Button
                data-testid="howto-register-btn"
                variant="outline"
                className="border-amber-500/40 text-amber-200 hover:bg-amber-900/20 font-ui px-6 h-12 bg-transparent"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Create free account
              </Button>
            </Link>
          </div>
        </header>

        {/* ===== Step-by-step ===== */}
        <section aria-labelledby="howto-steps" className="mb-20">
          <h2
            id="howto-steps"
            className="font-display text-2xl sm:text-3xl text-amber-100 mb-6 flex items-center gap-3"
          >
            <span className="font-ui text-[10px] uppercase tracking-widest text-amber-400/70">step-by-step</span>
            <span className="h-px flex-1 bg-amber-500/20" />
          </h2>
          <ol className="space-y-5">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <li
                  key={s.n}
                  data-testid={`howto-step-${s.n}`}
                  className="relative flex gap-5 p-5 rounded-lg border border-amber-800/30 bg-stone-950/40"
                >
                  <div className="shrink-0">
                    <div className="w-14 h-14 rounded-md border-2 border-amber-600/50 bg-stone-900 flex items-center justify-center font-display text-2xl text-amber-300">
                      {s.n}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-xl text-amber-100 flex items-center gap-2">
                      <Icon className="w-4 h-4 text-amber-400" />
                      {s.title}
                    </h3>
                    <p className="font-body text-amber-100/75 mt-2 leading-relaxed text-sm sm:text-[15px]">
                      {s.body}
                    </p>
                    {s.cta && (
                      <Link to={s.cta.to} className="inline-block mt-3">
                        <Button
                          variant="outline"
                          className="border-amber-500/40 text-amber-200 hover:bg-amber-900/20 font-ui h-9 px-4 bg-transparent text-xs"
                        >
                          {s.cta.label}
                        </Button>
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        {/* ===== Features ===== */}
        <section aria-labelledby="howto-features" className="mb-20">
          <h2
            id="howto-features"
            className="font-display text-2xl sm:text-3xl text-amber-100 mb-6 flex items-center gap-3"
          >
            <span className="font-ui text-[10px] uppercase tracking-widest text-amber-400/70">what makes it real D&amp;D</span>
            <span className="h-px flex-1 bg-amber-500/20" />
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  data-testid={`howto-feature-${i}`}
                  className="p-4 rounded-lg border border-amber-800/30 bg-stone-950/40"
                >
                  <Icon className="w-5 h-5 text-amber-400 mb-3" />
                  <h3 className="font-display text-lg text-amber-100 mb-1">{f.title}</h3>
                  <p className="font-body text-[13.5px] text-amber-100/70 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section aria-labelledby="howto-faq" className="mb-16">
          <h2
            id="howto-faq"
            className="font-display text-2xl sm:text-3xl text-amber-100 mb-6 flex items-center gap-3"
          >
            <span className="font-ui text-[10px] uppercase tracking-widest text-amber-400/70">frequently asked</span>
            <span className="h-px flex-1 bg-amber-500/20" />
          </h2>
          <dl className="space-y-4">
            {FAQ.map((f, i) => (
              <div
                key={i}
                data-testid={`howto-faq-${i}`}
                className="p-4 rounded-lg border border-amber-800/30 bg-stone-950/40"
              >
                <dt className="font-display text-lg text-amber-100 mb-1">{f.q}</dt>
                <dd className="font-body text-[14px] text-amber-100/70 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* ===== CTA ===== */}
        <section className="text-center pt-6">
          <p className="font-body text-amber-100/80 mb-5">
            That&apos;s everything. The candles are lit. The dice are ready.
          </p>
          <Link to="/trial">
            <Button
              data-testid="howto-final-cta"
              className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui px-8 h-12"
            >
              <Flame className="w-4 h-4 mr-2" /> Begin your saga
            </Button>
          </Link>
        </section>
      </main>
    </div>
  );
}
